import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { StateManagerProjectService } from '@state-manager/state-manager-project.service';

import { CornerstoneService } from '@viewer/cornerstone.service';
import {
  ColorsMenuComponentKeyActionName,
  CornerstoneTool,
  HotKey,
  HotKeySettings,
  ViewWindowIndex,
  ColorsMenuComponentKeyActionNameList
} from '@viewer/data';
import { combineLatest, map, Observable, Subject, takeUntil } from 'rxjs';
import { COLOR_NAMES, IColorData } from './colors.data';
import Utils from '@shared/utils';
import { KeyInteractionService } from 'src/app/interaction';
import { CASE_TYPE } from '@viewer/cornerstone.data';

@Component({
  selector: 'app-colors-menu',
  templateUrl: 'colors-menu.component.html',
  styleUrls: ['colors-menu.component.scss'],
})
export class ColorsMenuComponent implements OnInit, OnDestroy {
  readonly ViewWindowIndex = ViewWindowIndex;
  readonly CornerstoneTool = CornerstoneTool;
  public colorsData: {
    viewer1: IColorData[];
    viewer2: IColorData[];
    viewer3: IColorData[];
  } = {
    viewer1: [],
    viewer2: [],
    viewer3: [],
  };

  public isDisabledColorMenu$!: Observable<boolean>;

  private _unsubscribe$ = new Subject();

  constructor(
    public stateManagerProjectService: StateManagerProjectService,
    private _cornerstoneService: CornerstoneService,
    private _cdr: ChangeDetectorRef,
    private _keyInteractionService: KeyInteractionService
  ) {
    this.isDisabledColorMenu$ = combineLatest([
      this.stateManagerProjectService.currentTool$,
      this.stateManagerProjectService.isActiveShowChangesMode$,
      stateManagerProjectService.currentSelectedWindow$,
    ]).pipe(
      map(
        ([currentTool, isActiveShowChangesMode, currentSelectedWindow]) =>
          [
            CornerstoneTool.CircleBrush,
            CornerstoneTool.SquareBrush,
            CornerstoneTool.EraseCircleBrush,
            CornerstoneTool.EraseSquareBrush,
            CornerstoneTool.CustomPolylineFreehand,
            CornerstoneTool.ShowDiff,
          ].includes(currentTool!) || isActiveShowChangesMode!
      )
    );
  }

  ngOnInit(): void {
    this.stateManagerProjectService.colorsData$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((data) => {
        if (data !== null) {
          this.colorsData = data;
        } else {
          this.colorsData = {
            viewer1: [],
            viewer2: [],
            viewer3: [],
          };
        }
      });
    this.stateManagerProjectService.currentActiveColor$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((_) => this._cdr.markForCheck());
    this._initializeHotKeys();
  }

  public selectColor(index: number): void {
    if (this.stateManagerProjectService.isViewerLoaded) {
      if (index != this._cornerstoneService.activeLabel) {
        this._cornerstoneService.switchLabelMap(index);
      }
    }
  }

  public hideSegmentationLayer(
    color: IColorData,
    index: number,
    event?: MouseEvent | TouchEvent
  ): void {
    color.state
      ? this._cornerstoneService.changeVisibilityOfColor(
          color.indexOfSegment,
          false
        )
      : this._cornerstoneService.changeVisibilityOfColor(
          color.indexOfSegment,
          true
        );
    const colorData = this.stateManagerProjectService.colorsData;
    for (let i = 1; i < 4; i++) {
      const colorDataForWindow = Utils.deepClone(colorData![`viewer${i}`]);
      colorDataForWindow[index].state = !color.state;
      colorData![`viewer${i}`] = colorDataForWindow;
    }
    this.stateManagerProjectService.colorsData = colorData;
    if (event !== undefined) {
      event.stopPropagation();
    }
  }

  public getColorName(index: number): string {
    const TIBIA_LABEL_INDEX = 3;
    index = this.stateManagerProjectService.caseType === CASE_TYPE.FEMUR ? index : TIBIA_LABEL_INDEX;
    return COLOR_NAMES[index];
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next(undefined);
    this._unsubscribe$.complete();
    this._keyInteractionService.removeKeySettingsList([
      ...ColorsMenuComponentKeyActionNameList
    ]);
  }

  private _initializeHotKeys(): void {
    this._keyInteractionService
      .initializeHotKey(
        HotKey.Zero,
        new HotKeySettings<ColorsMenuComponentKeyActionName>({
          keyActionName: 'ShowHideTrigger',
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        const INDEX = 0;
        this.hideSegmentationLayer(
          this.colorsData['viewer1'][INDEX],
          INDEX
        );
      });
  }
}
